import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router, useForm } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type BoardMember = {
  id?: number;
  name: string;
  role: string;
  bio: string;
  image_url: string;
  // avatar_url?: string;
  email: string;
  linkedin: string;
  website: string;
  category: string;
  board_type?: string;
  position: number;
};

type Props = {
  board_member: BoardMember;
  categories: { value: string; label: string }[];
  errors?: string[];
};

export default function BoardMemberForm({
  board_member,
  categories,
  errors,
}: Props) {
  const { data, setData, processing, patch, post, transform } = useForm({
    name: board_member.name || '',
    role: board_member.role || '',
    bio: board_member.bio || '',
    // image_url: board_member.image_url || '',
    avatar: null as File | null,
    email: board_member.email || '',
    linkedin: board_member.linkedin || '',
    website: board_member.website || '',
    category: board_member.category || 'executive',
    board_type: board_member.board_type || 'executive',
    position: board_member.position || 0,
  });

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    transform((data) => ({
      board_member: data,
    }));

    if (board_member.id) {
      patch(`/admin/board_members/${board_member.id}`, {
        forceFormData: true,
      });
    } else {
      post('/admin/board_members', {
        forceFormData: true,
      });
    }
  };

  return (
    <AdminLayout
      title={board_member.id ? 'Edit Board Member' : 'New Board Member'}
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/board_members"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Board Members
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {board_member.id ? 'Edit Board Member' : 'New Board Member'}
          </h1>
        </div>

        {/* Error messages */}
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">
              Please fix the following errors:
            </p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white shadow rounded-lg p-6"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="role">Role *</Label>
              <Input
                id="role"
                type="text"
                value={data.role}
                onChange={(e) => setData('role', e.target.value)}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="board_type">Board Type *</Label>
              <Select
                value={data.board_type}
                onValueChange={(value) => setData('board_type', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select board type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="executive">Executive Board</SelectItem>
                  <SelectItem value="directors">Board of Directors</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select
                value={data.category}
                onValueChange={(value) => setData('category', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={data.bio}
                onChange={(e) => setData('bio', e.target.value)}
                rows={4}
                className="mt-1"
                placeholder="Brief biography or description..."
              />
            </div>

            {/* <div className="sm:col-span-2">
              <Label htmlFor="image_url">Profile Image URL</Label>
              <Input
                id="image_url"
                type="url"
                value={data.image_url}
                onChange={(e) => setData('image_url', e.target.value)}
                className="mt-1"
                placeholder="https://example.com/image.jpg"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty to use default avatar
              </p>
            </div> */}
            <div className="sm:col-span-2">
              <Label htmlFor="avatar">Profile Photo</Label>
              <div className="mt-1 flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100">
                  <img
                    src={avatarPreview || board_member.image_url}
                    alt={board_member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <Input
                    id="avatar"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setData('avatar', file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setAvatarPreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    JPG, PNG or GIF (max 5MB)
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                className="mt-1"
                placeholder="email@example.com"
              />
            </div>

            <div>
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input
                id="linkedin"
                type="url"
                value={data.linkedin}
                onChange={(e) => setData('linkedin', e.target.value)}
                className="mt-1"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div>
              <Label htmlFor="website">Website URL</Label>
              <Input
                id="website"
                type="url"
                value={data.website}
                onChange={(e) => setData('website', e.target.value)}
                className="mt-1"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="position">Position (Order)</Label>
              <Input
                id="position"
                type="number"
                value={data.position}
                onChange={(e) =>
                  setData('position', parseInt(e.target.value) || 0)
                }
                className="mt-1"
                min="0"
              />
              <p className="mt-1 text-xs text-gray-500">
                Lower numbers appear first
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" asChild>
              <Link href="/admin/board_members">Cancel</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Board Member'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
